import { formatHHMM, parseHHMM } from '@/utilities/hhmm';
import { serviceTypeLabel } from '@/constants/serviceTypes';

// Form <-> catalog-row mapping for ServiceCatalogModal. Kept pure so the payload shape
// (which must line up with UPSERT_SERVICE_CATALOG's update_columns) is unit-testable.

const trimmedOrNull = (value) => `${value ?? ''}`.trim() || null;

// Normalize a row's `dimensions` jsonb to a flat { key: 'value' } map, dropping blanks so
// an empty selection clears the key rather than storing ''.
const normalizeDimensions = (dimensions) => {
    if (!dimensions || typeof dimensions !== 'object' || Array.isArray(dimensions)) return {};
    return Object.entries(dimensions).reduce((acc, [key, value]) => {
        const trimmed = `${value ?? ''}`.trim();
        return key && trimmed ? { ...acc, [key]: trimmed } : acc;
    }, {});
};

const upper = (value) => `${value ?? ''}`.trim().toUpperCase();

// A service rate as the Rate field reports it: dollars, or null for none. Null is checked first
// because Number(null) is 0.
const toRate = (value) => {
    if (value === null || value === undefined || `${value}`.trim() === '') return null;
    const rate = Number(value);
    return Number.isFinite(rate) && rate >= 0 ? rate : null;
};

export const NON_BILLABLE_SERVICE_TYPE = 'non_billable';
export const SERVICE_RATE_TYPE = 'SERVICE_RATE';

// A scoped service added here has no rate row (site pricing is seeded by hand), and the GE algo
// throws SERVICE_PRICING_FAILURE for the whole order on any unpriceable line — so a new scoped
// service is forced non-billable. Edit mode is exempt: the seeded rows are scoped AND billable,
// and forcing them would silently zero real pricing. So is a shipper with the catalog-service-rates
// flag: the service's rate prices it instead.
export const scopedNonBillableApplies = (values, { isEdit, serviceRatesEnabled } = {}) =>
    !isEdit && !serviceRatesEnabled && Object.keys(normalizeDimensions(values?.dimensions)).length > 0;

// Derived, not stored, so clearing the scope restores whatever type the operator picked.
export const applyScopedNonBillable = (values, options) =>
    scopedNonBillableApplies(values, options)
        ? { ...values, serviceType: NON_BILLABLE_SERVICE_TYPE, billable: false }
        : values;

// 'site' => 'Site', 'service_area' => 'Service Area'.
export const dimensionLabel = (key) =>
    `${key ?? ''}`
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ');

// Options derived from existing entries since they're seeded currently
export const dimensionOptions = (services = []) => {
    const valuesByKey = new Map();

    services.forEach((service) => {
        Object.entries(normalizeDimensions(service?.dimensions)).forEach(([key, value]) => {
            if (!valuesByKey.has(key)) valuesByKey.set(key, new Set());
            valuesByKey.get(key).add(value);
        });
    });

    return [...valuesByKey.keys()].sort().map((key) => ({
        key,
        label: dimensionLabel(key),
        values: [...valuesByKey.get(key)].sort(),
    }));
};

// GE rate rows are scoped by site alone (dimensions.site), so only an unscoped or site-scoped entry
// can carry a rate: { site } for one, {} for the other, null for any other scope.
const rateScope = (dimensions) => {
    const scope = normalizeDimensions(dimensions);
    if (Object.keys(scope).some((key) => key !== 'site')) return null;
    return scope.site ? { site: scope.site } : {};
};

export const serviceRateScopeSupported = (dimensions) => rateScope(dimensions) !== null;

// A catalog entry's SERVICE_RATE row among the shipper's: the same code at exactly the entry's scope
// (its site, or no site for an unscoped entry), matched on read the way the Tariffs editor matches a
// service to its row. The first match when the shipper's tariffs disagree; a save rewrites them all.
export const findServiceRate = ({ code, dimensions } = {}, rows = []) => {
    const scope = rateScope(dimensions);
    if (!scope || !upper(code)) return null;
    return (
        rows.find(
            (row) => upper(row?.dimensions?.code) === upper(code) && upper(row?.dimensions?.site) === upper(scope.site)
        ) || null
    );
};

// Seed the modal's fields from a catalog row (or blanks in add mode), and the rate fields from the
// entry's SERVICE_RATE row. Service time is edited as HH:MM; formatHHMM yields '' for an unset value
// so it can seed a text input.
export const serviceCatalogFormValues = (service, serviceRate) => ({
    code: service?.service_code || '',
    description: service?.description || '',
    serviceType: service?.service_type || '',
    size: service?.size || '',
    requireConfirm: !!service?.require_confirm,
    // Catalog rows default billable=true, so only an explicit false unchecks the toggle.
    billable: service?.billable !== false,
    serviceTime: formatHHMM(service?.service_time),
    dimensions: normalizeDimensions(service?.dimensions),
    rate: toRate(serviceRate?.rate),
    // Not editable here: a row's qbo_tag is its code unless it was set to something else outside the
    // modal, and that one is carried through so a save doesn't reset it to the code.
    qboTag:
        serviceRate?.qbo_tag && upper(serviceRate.qbo_tag) !== upper(service?.service_code) ? serviceRate.qbo_tag : '',
});

// The modal's Rate field: off, with why, until the shipper's tariffs and rates are `ready` (the Rate
// seeds from them; after a failed lookup a save leaves the rows alone, see useSaveServiceCatalog), and
// then unless there's a tariff for the rate to go on and a scope a rate row can carry. Also off while
// Billable is, since a non-billable line never charges it.
export const serviceRateFieldState = ({ billable, dimensions, tariffIds = [], ready = false, error = null } = {}) => {
    if (error) return { disabled: true, helperText: "Unable to load your tariffs. Saving won't change the rate." };
    if (!ready) return { disabled: true, helperText: 'Loading your tariffs…' };
    if (tariffIds.length === 0) return { disabled: true, helperText: 'No tariff to add a rate to.' };
    if (!serviceRateScopeSupported(dimensions)) {
        return { disabled: true, helperText: 'A rate can only be set on an unscoped or site-scoped service.' };
    }
    if (!billable) return { disabled: true, helperText: 'Not charged while Billable is off.' };
    return { disabled: false, helperText: 'Added to your tariffs. Charged when the service has no other rate row.' };
};

// The grid's Rate cell.
export const formatServiceRate = (rate) => {
    const value = toRate(rate);
    return value === null ? '--' : `$${value.toFixed(2)}`;
};

// dimension_key joins key/value with ASCII unit (31) / record (30) separators
// (rates_canonical_key). Render them as readable delimiters, e.g. "site 5ZR".
export const formatDimensionKey = (dimKey) =>
    (dimKey || '').split(String.fromCharCode(31)).join(' ').split(String.fromCharCode(30)).join(', ');

// The grid's search. Every whitespace-separated term must appear, case-insensitively, in the row's
// text columns as the grid shows them (Type by its label or raw value), so "M001 5ZR" narrows to
// that code at that site.
export const filterServiceCatalog = (services = [], query = '') => {
    const terms = `${query ?? ''}`.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return services;

    return services.filter((service) => {
        const text = [
            service?.service_code,
            service?.description,
            service?.service_type,
            serviceTypeLabel(service?.service_type),
            formatDimensionKey(service?.dimension_key),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return terms.every((term) => text.includes(term));
    });
};

// Build the upsert payload. Every key here must also appear in UPSERT_SERVICE_CATALOG's
// update_columns, or the field saves on create and silently no-ops on edit.
export const buildServiceCatalogPayload = (values) => ({
    // Upper-cased so the code is canonical: ingest looks rows up by an uppercased code, and the
    // `_in` filter it uses can't match case-insensitively.
    service_code: `${values.code ?? ''}`.trim().toUpperCase(),
    description: trimmedOrNull(values.description),
    service_type: trimmedOrNull(values.serviceType),
    size: trimmedOrNull(values.size),
    require_confirm: !!values.requireConfirm,
    billable: !!values.billable,
    service_time: parseHHMM(values.serviceTime),
    // dimension_key is a generated column (rates_canonical_key(dimensions)) — write only
    // `dimensions` and Postgres derives the key. The column is not-null, so send {} not null.
    // Create-only: absent from the upsert's update_columns, so an edit's copy is ignored.
    dimensions: normalizeDimensions(values.dimensions),
});

// A save's change to the shipper's GE tariffs (UPSERT_SERVICE_CATALOG_WITH_RATES): the service's
// SERVICE_RATE rows to clear, and its new row on each tariff, shaped like the seeded GE service rows
// (qbo_tag the code, or the one the row already carried; label the description; open bounds; and the
// entry's site, code and type in dimensions). A blank rate only clears them, as the Tariffs editor
// drops a blank row. Null when there's nothing to write: no code, no tariff, or a scope a rate row
// can't carry. `object` is buildServiceCatalogPayload's.
export const buildServiceRateChange = (object, { rate, qboTag } = {}, tariffIds = []) => {
    const code = upper(object?.service_code);
    const scope = rateScope(object?.dimensions);
    const ids = (tariffIds || []).filter(Boolean);
    if (!code || !scope || ids.length === 0) return null;

    const value = toRate(rate);
    return {
        // Anchored on the tariffs, the type, the code and the exact scope, so it can only reach this
        // service's rows: an empty bool_exp would match every rate.
        stale_rates: {
            _and: [
                { tariff_id: { _in: ids } },
                { type: { _eq: SERVICE_RATE_TYPE } },
                { dimensions: { _contains: { code } } },
                scope.site
                    ? { dimensions: { _contains: { site: scope.site } } }
                    : { _not: { dimensions: { _has_key: 'site' } } },
            ],
        },
        rates:
            value === null
                ? []
                : ids.map((tariff_id) => ({
                      tariff_id,
                      type: SERVICE_RATE_TYPE,
                      rate: value,
                      qbo_tag: trimmedOrNull(qboTag) || code,
                      label: trimmedOrNull(object.description) || code,
                      min: 0,
                      max: 'infinity',
                      min2: 0,
                      max2: 'infinity',
                      subregion_id: null,
                      service_level_id: null,
                      dimensions: {
                          ...scope,
                          code,
                          ...(trimmedOrNull(object.service_type)
                              ? { service_type: trimmedOrNull(object.service_type) }
                              : {}),
                      },
                  })),
    };
};
